import React from 'react';
import { View, Text, Image } from 'react-native';
import { TouchableOpacity } from 'react-native-gesture-handler';
import { useTranslation } from 'react-i18next';
import { SuccessModal } from '@double_point/rn-components';
import OrderDescription from 'types/models/OrderDescription';
import OrderItemProps from 'types/components/OrderItemProps';
import CommerceImageTypes from 'types/enums/CommerceImageTypes';
import OrderStatuses from 'types/enums/OrderStatuses';
import AppContext from 'contexts/AppContext';
import { translateOrderDescription } from 'services/ordersService';
import { refundSitefPayment } from 'services/paymentsService';
import { formatOrderId, getcolorStatusDescription, getPaymentMethod } from 'utils/orders';
import { valueToCurrencyFormat } from 'utils/parseCurrency';
import ConfirmationModal from './ConfirmationModal';
import { greenColor } from 'styles/global';
import { styles } from 'styles/components/orderItem';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default ({ order, onClickDetail, setRefreshList }: OrderItemProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { currentVenue, setModalizeData, setIsLoading } = React.useContext(AppContext);
  const imageUri =
    order.commerce.images.find((image) => image.commerceImageTypeId === CommerceImageTypes.logo)?.path ??
    'https://via.placeholder.com/100';

  const orderDescriptions: OrderDescription = {
    orderCreated: t('pending-payment'),
    orderPaymentAccepted: t('payment-accepted'),
    orderPreparing: t('preparing-order'),
    orderToDeliver: t('order-ready-to-deliver'),
    orderDelivering: t('delivering-order'),
    orderDelivered: t('order-delivered'),
    orderIncompleted: t('order-incompleted'),
    orderCanceled: t('order-canceled'),
    orderAddedToCheckingAccount: t('order-added-to-checking-account'),
  };
  const { isCardPayment, isFiservQrPayment, isMPCheckoutPayment } = getPaymentMethod(order);

  const canCancelOrder = () => {
    const validStatuses = [
      OrderStatuses.paymentAccepted,
      OrderStatuses.toDeliver,
      OrderStatuses.preparing,
      OrderStatuses.delivering,
    ];
    if (!validStatuses.includes(order.orderStatusId)) return false;
    if (order.payment?.isCourtesy) return false;
    if (!isCardPayment && !isFiservQrPayment) return false;
    if (isMPCheckoutPayment) return false;
    return true;
  };

  const cancelOrder = async () => {
    setIsLoading(true);
    let isSuccess = false;

    try {
      const res = await refundSitefPayment(order.id);
      isSuccess = Number(res.code) === 0;
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }

    setModalizeData({
      content: (
        <SuccessModal
          isSuccess={isSuccess}
          title={t('refund')}
          mainColor={currentVenue?.appColor ?? '#001C47'}
          message={t(isSuccess ? 'order-refund-success-with-id' : 'order-refund-error-with-id', { id: order.id })}
          buttonText={t('accept')}
          onConfirm={() => setModalizeData(null)}
        />
      ),
      closeOnOverlayTap: true,
    });

    if (isSuccess) {
      await delay(2 * 1000);
      setRefreshList(true);
    }
  };

  const handleConfirmAction = () => {
    setModalizeData({
      content: (
        <ConfirmationModal
          cancelButtonText={t('cancel')}
          confirmButtonText={t('confirm-code')}
          onConfirm={async () => await cancelOrder()}
          onCancel={() => setModalizeData(null)}
          message={t('cancel-order-confirmation')}
        />
      ),
      closeOnOverlayTap: true,
    });
  };

  return (
    <View style={styles.productInfo}>
      <View style={[styles.productInfoColumn]}>
        <Image
          style={styles.productImage}
          source={{
            uri: imageUri,
          }}
        />
      </View>
      <View style={[styles.productInfoColumn, styles.nameColumn]}>
        <View style={{ flex: 1 }}>
          <Text
            style={{
              flex: 1,
              fontStyle: 'normal',
              fontWeight: '500',
              fontSize: 14,
              lineHeight: 24,
            }}
          >
            {`${t('order-detail-number')} ${formatOrderId(order.id, order.orderStatusId)}`}
          </Text>
          <Text
            style={[
              { borderColor: 'red', flex: 1, fontWeight: '500', textTransform: 'uppercase' },
              { color: getcolorStatusDescription(order.orderStatusId) },
            ]}
          >
            {translateOrderDescription(order.orderStatusId, orderDescriptions)}
          </Text>
        </View>
        <View style={{ flex: 1, display: 'flex', flexDirection: 'row', minHeight: 'max-content' }}>
          <View style={{ flex: 2.5 }}>
            <Text
              style={{
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: 12,
                lineHeight: 20,
              }}
            >{`${order.commerce.name}`}</Text>
            <Text
              style={{
                fontStyle: 'normal',
                fontWeight: '700',
                fontSize: 14,
                lineHeight: 20,
              }}
            >
              {valueToCurrencyFormat(order.totalAmount, i18n.language)}
            </Text>
          </View>

          <View style={{ flex: 2, justifyContent: 'center', alignItems: 'center', gap: 4, marginBottom: 12 }}>
            <TouchableOpacity
              style={[styles.button, { backgroundColor: currentVenue?.appColor ?? greenColor }]}
              onPress={() => onClickDetail(order.id)}
            >
              <Text style={styles.buttonText}>{t('go-to-order')}</Text>
            </TouchableOpacity>

            {canCancelOrder() && (
              <TouchableOpacity style={[styles.button, { backgroundColor: '#F00' }]} onPress={handleConfirmAction}>
                <Text style={styles.buttonText}>{t('cancel')}</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};
